import { Autocomplete, AutocompleteProps, createFilterOptions, Paper, PaperProps } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";

import { JobOption, ShowAllLabels } from "./showAllLabels";

const OTHERS = "others";

type ShowAllPaperProps = PaperProps & { footer?: ReactNode };

const ShowAllPaper = ({ footer, children, ...rest }: ShowAllPaperProps) => (
  <Paper {...rest}>
    {children}
    {footer}
  </Paper>
);

type ShowAllAutocompleteProps<T extends JobOption> =
  AutocompleteProps<T, false, boolean | undefined, false> & { labels: ShowAllLabels };

export const ShowAllAutocomplete = <T extends JobOption,>({ labels, options, ...rest }: ShowAllAutocompleteProps<T>) => {
  const [showingAll, setShowingAll] = useState(false);
  const expanded = showingAll || rest.value?.outsideUsualJob === true;

  const sorted = useMemo(
    () => [...options.filter(o => !o.outsideUsualJob), ...options.filter(o => o.outsideUsualJob)],
    [options]
  );
  const filter = useMemo(() => createFilterOptions<T>(), []);
  const hasOthers = sorted.some(o => o.outsideUsualJob);

  const footer = hasOthers && !expanded
    ? (
      <div
        className="bg-theme-info px-3 py-1 text-sm font-semibold text-white cursor-pointer"
        onMouseDown={e => e.preventDefault()}
        onClick={() => setShowingAll(true)}
      >
        {labels.showAll}…
      </div>
    )
    : null;

  return (
    <Autocomplete
      {...rest}
      options={sorted}
      filterOptions={(opts, state) => {
        const filtered = filter(opts, state);
        return expanded || state.inputValue !== "" ? filtered : filtered.filter(o => !o.outsideUsualJob);
      }}
      groupBy={option => option.outsideUsualJob ? OTHERS : ""}
      renderGroup={params => (
        <li key={params.key}>
          {params.group === OTHERS && (
            <div className="bg-theme-info px-3 py-1 text-sm font-semibold text-white">{labels.others}</div>
          )}
          <ul>{params.children}</ul>
        </li>
      )}
      slots={{ paper: ShowAllPaper }}
      slotProps={{ paper: { footer } as PaperProps }}
    />
  );
};
